import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile } from 'node:fs/promises'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'

const execFileAsync = promisify(execFile)

/** Launcher for Anthropic's Claude Code CLI */
export class ClaudeCodeLauncher implements ProviderLauncher {
  readonly name = 'claude-code'
  readonly displayName = 'Claude Code'
  readonly pricingHint = '💰 Abonnement payant (Anthropic Pro/API requis)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('claude', ['--version'])
      return true
    } catch {
      return false
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('claude', ['--version'])
      return stdout.trim()
    } catch {
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const args: string[] = []

    const systemPrompt = config.systemPromptContent ?? await readFile(config.systemPromptPath, 'utf-8')
    args.push('--append-system-prompt', systemPrompt)

    args.push('--permission-mode', config.permissionMode ?? 'default')

    if (config.model) {
      args.push('--model', config.model)
    }

    if (config.disallowedTools && config.disallowedTools.length > 0) {
      args.push('--disallowedTools', ...config.disallowedTools)
    }

    const recorder = new SessionRecorder()

    const ptyProc = getPty().spawn(await resolveBinaryOrFallback('claude'), args, {
      name: 'xterm-256color',
      cwd: workdir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.
    // The system prompt already contains the briefing and candidate-driven
    // workflow rules. The candidate reads the briefing before the AI launches.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(({ exitCode, signal }) => {
            cleanup()
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
      },
      recorder,
    }
  }
}
